import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Layers, Plus } from "lucide-react";
import {
  Alert,
  Button,
  StatusPill,
  Skeleton,
  Input,
  TabsRoot,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@flint/ui";
import { StoreAdminLayout } from "../store-admin-layout.tsx";
import { storeErrorMessage } from "../error-message.ts";
import { trpc } from "../../../lib/trpc.ts";
import { usePermission } from "../../../lib/permissions.ts";
import {
  PHASE_LABEL,
  PHASE_TONE,
  TYPE_LABEL,
  phaseWithDates,
  seasonWindow,
} from "./collection-format.ts";

type Phase = "all" | "live" | "upcoming" | "ended" | "archived";
const TABS: { key: Phase; label: string }[] = [
  { key: "all", label: "All" },
  { key: "live", label: "Live" },
  { key: "upcoming", label: "Upcoming" },
  { key: "ended", label: "Ended" },
  { key: "archived", label: "Archived" },
];
const EMPTY_COUNTS: Record<Phase, number> = {
  all: 0,
  live: 0,
  upcoming: 0,
  ended: 0,
  archived: 0,
};

/** Columns in the table — keeps the skeleton and empty-state `colSpan` honest. */
const COL_COUNT = 6;
const PAGE_SIZE = 25;

/**
 * Store admin — Collections list (Commerce, M6).
 *
 * The wireframe drew only a search box. Tabs and counts are added on top: the
 * phase is the thing an operator scans for ("what's live right now?"), and
 * without an Archived tab an archived collection had no route back.
 *
 * @see prototype/store/admin/all.html#collections-list
 */
export function CollectionsListPage() {
  const [phase, setPhase] = useState<Phase>("all");
  const [q, setQ] = useState("");
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors.at(-1);
  const canManage = usePermission("store.catalog.manage");

  const list = trpc.storeCollections.list.useQuery({
    phase,
    limit: PAGE_SIZE,
    includeArchived: phase === "archived",
    ...(q ? { q } : {}),
    ...(cursor ? { cursor } : {}),
  });
  // Tab-independent, so the badges don't flicker when the operator switches.
  const counts = trpc.storeCollections.counts.useQuery();
  const tabCounts = counts.data ?? EMPTY_COUNTS;
  const rows = list.data?.items ?? [];

  const resetPaging = () => setCursors([]);

  return (
    <StoreAdminLayout
      breadcrumbs={[{ label: "Store", to: "/store/admin" }, { label: "Collections" }]}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-ink leading-tight">Collections</h1>
          <p className="mt-1.5 text-sm text-muted">
            Your line sheets — the always-orderable Core catalog and each seasonal drop.
          </p>
        </div>
        {canManage ? (
          <Button asChild intent="primary" size="md">
            <Link to="/store/admin/collections/new">
              <Plus size={14} aria-hidden />
              New collection
            </Link>
          </Button>
        ) : null}
      </div>

      <div className="mt-4 overflow-hidden rounded border border-line bg-paper shadow">
        <TabsRoot
          value={phase}
          onValueChange={(v) => {
            setPhase(v as Phase);
            resetPaging();
          }}
        >
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-line px-4">
            {/* `overflow-y-hidden`: the primitive's `overflow-x-auto` also
                computes `overflow-y: auto`, and the count badges make the list
                a pixel taller than its box — enough for a stray scrollbar. */}
            <TabsList aria-label="Collection phase" className="border-b-0 overflow-y-hidden">
              {TABS.map((t) => (
                <TabsTrigger key={t.key} value={t.key} count={tabCounts[t.key]}>
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
            <div className="flex flex-wrap items-center gap-2 py-2">
              <Input
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  resetPaging();
                }}
                placeholder="Search collections…"
                className="h-8 w-full min-w-60 sm:w-72"
              />
            </div>
          </div>
          {/* Each trigger points `aria-controls` at a panel id; the table below
              isn't a Radix panel, so without these the references dangle. */}
          {TABS.map((t) => (
            <TabsContent key={t.key} value={t.key} forceMount className="hidden" aria-hidden />
          ))}
        </TabsRoot>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-left text-sm">
            <thead className="border-b border-line bg-raised text-xs uppercase tracking-wide text-muted">
              <tr>
                <th className="w-14 px-3 py-4">
                  <span className="sr-only">Cover</span>
                </th>
                <th className="px-3 py-4 text-left font-medium">Collection</th>
                <th className="px-3 py-4 text-left font-medium">Type</th>
                <th className="px-3 py-4 text-right font-medium">Products</th>
                <th className="px-3 py-4 text-left font-medium">Season window</th>
                <th className="px-3 py-4 text-left font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {list.isLoading
                ? Array.from({ length: 4 }).map((_, i) => (
                    <tr key={i} className="border-b border-line">
                      <td className="px-3 py-3" colSpan={COL_COUNT}>
                        <Skeleton className="h-6 w-full" />
                      </td>
                    </tr>
                  ))
                : rows.map((c) => (
                    <tr
                      key={c.collectionId}
                      className="border-b border-line last:border-0 hover:bg-raised"
                    >
                      <td className="px-3 py-3">
                        {c.imageUrl ? (
                          <img
                            src={c.imageUrl}
                            alt=""
                            className="h-9 w-9 rounded-sm border border-line object-cover"
                          />
                        ) : (
                          <span className="flex h-9 w-9 items-center justify-center rounded-sm border border-line bg-raised text-muted">
                            <Layers className="h-4 w-4" aria-hidden />
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <Link
                          to="/store/admin/collections/$collectionId"
                          params={{ collectionId: c.collectionId }}
                          className="font-medium text-ink hover:text-sky-700"
                        >
                          {c.name}
                        </Link>
                        {c.description ? (
                          <div className="mt-0.5 line-clamp-1 text-xs text-muted">
                            {c.description}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-3 py-3 text-ink-2">{TYPE_LABEL[c.type]}</td>
                      <td className="px-3 py-3 text-right font-mono tabular-nums text-ink">
                        {c.productCount}
                      </td>
                      <td className="px-3 py-3">
                        {c.activeFrom || c.activeTo ? (
                          <span className="font-mono tabular-nums text-sm text-ink-2">
                            {seasonWindow(c.activeFrom, c.activeTo)}
                          </span>
                        ) : (
                          // `seasonWindow` owns the no-window wording ("Always
                          // on") — Core has no window by definition, and an
                          // em-dash would read as data someone forgot to enter.
                          // Only the styling differs: dates are mono, this isn't.
                          <span className="text-muted">{seasonWindow(null, null)}</span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        {(() => {
                          const hint = phaseWithDates(c.phase, c.activeFrom, c.activeTo);
                          return (
                            <StatusPill
                              tone={PHASE_TONE[c.phase]}
                              size="sm"
                              dot
                              {...(hint ? { title: hint } : {})}
                            >
                              {PHASE_LABEL[c.phase]}
                            </StatusPill>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
              {/* Error before empty. A failed read and an empty catalog both
                  leave `rows` at zero, and telling an operator "no collections
                  yet — create one" when the server just refused the request
                  invites them to rebuild data that is still there. */}
              {!list.isLoading && list.isError ? (
                <tr>
                  <td colSpan={COL_COUNT} className="px-3 py-8">
                    <Alert
                      intent="bad"
                      title="Couldn't load collections"
                      description={storeErrorMessage(list.error)}
                    >
                      <Button
                        intent="secondary"
                        size="sm"
                        className="mt-2.5"
                        disabled={list.isFetching}
                        onClick={() => void list.refetch()}
                      >
                        {list.isFetching ? "Retrying…" : "Try again"}
                      </Button>
                    </Alert>
                  </td>
                </tr>
              ) : null}

              {!list.isLoading && !list.isError && rows.length === 0 ? (
                <tr>
                  <td colSpan={COL_COUNT} className="px-3 py-12 text-center text-sm text-muted">
                    {q
                      ? "No collections match that search."
                      : phase === "all"
                        ? "No collections yet. Start with Core — your always-orderable catalog — then add seasonal drops."
                        : `No ${PHASE_LABEL[phase as keyof typeof PHASE_LABEL].toLowerCase()} collections.`}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line bg-raised px-4 py-2.5 text-xs text-muted">
          <span>
            Showing <span className="font-mono font-semibold text-ink">{rows.length}</span>{" "}
            {rows.length === 1 ? "collection" : "collections"}
          </span>
          <div className="flex items-center gap-1.5">
            <Button
              intent="secondary"
              size="sm"
              disabled={cursors.length === 0}
              onClick={() => setCursors((c) => c.slice(0, -1))}
            >
              ← Prev
            </Button>
            <Button
              intent="secondary"
              size="sm"
              disabled={!list.data?.nextCursor}
              onClick={() => {
                const next = list.data?.nextCursor;
                if (next) setCursors((c) => [...c, next]);
              }}
            >
              Next →
            </Button>
          </div>
        </div>
      </div>
    </StoreAdminLayout>
  );
}
